'use client'

import { useState, useMemo } from 'react'
import { cn } from '@paitox/ui/lib/utils'
import { Search, Plus, Gift, Tag, Trash2, Edit, CalendarDays, ShoppingBag } from 'lucide-react'
import { db, Package as DbPackage, ClientPackage as DbClientPackage } from '../lib/db'

export function PackagesPage() {
  const [packages, setPackages] = useState<DbPackage[]>(() => db.getPackages())
  const [clientPackages, setClientPackages] = useState<DbClientPackage[]>(() => db.getClientPackages())
  const [activeTab, setActiveTab] = useState<'offers' | 'purchases'>('offers')
  const [search, setSearch] = useState('')
  const [showNewOffer, setShowNewOffer] = useState(false)
  const [showNewPurchase, setShowNewPurchase] = useState(false)

  // Creation fields (offers)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [serviceId, setServiceId] = useState('')
  const [sessionsTotal, setSessionsTotal] = useState(5)
  const [price, setPrice] = useState(150000)
  const [validityDays, setValidityDays] = useState(180)

  // Purchase fields (client buy)
  const [clientId, setClientId] = useState('')
  const [purchasePkgId, setPurchasePkgId] = useState('')

  const services = useMemo(() => db.getServices(), [])
  const clients = useMemo(() => db.getClients(), [])

  const filteredOffers = useMemo(() => {
    if (!search) return packages
    return packages.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.serviceName.toLowerCase().includes(search.toLowerCase()))
  }, [packages, search])

  const filteredPurchases = useMemo(() => {
    if (!search) return clientPackages
    return clientPackages.filter(cp => cp.clientName.toLowerCase().includes(search.toLowerCase()) || cp.packageName.toLowerCase().includes(search.toLowerCase()))
  }, [clientPackages, search])

  const handleCreateOffer = () => {
    if (!name || !serviceId) return
    const srv = services.find(s => s.id === serviceId)
    if (!srv) return

    const newOffer: DbPackage = {
      id: `pkg-${Date.now()}`,
      name,
      description,
      serviceId,
      serviceName: srv.name,
      sessionsTotal,
      price,
      validityDays,
      isActive: true
    }

    const updated = [...packages, newOffer]
    setPackages(updated)
    db.savePackages(updated)
    setShowNewOffer(false)

    setName('')
    setDescription('')
    setServiceId('')
    setSessionsTotal(5)
    setPrice(150000)
    setValidityDays(180)
  }

  const handlePurchase = () => {
    if (!clientId || !purchasePkgId) return
    const cl = clients.find(c => c.id === clientId)
    const pkg = packages.find(p => p.id === purchasePkgId)
    if (!cl || !pkg) return

    const newPurchase: DbClientPackage = {
      id: `cp-${Date.now()}`,
      clientId,
      clientName: `${cl.firstName} ${cl.lastName}`,
      packageId: purchasePkgId,
      packageName: pkg.name,
      sessionsUsed: 0,
      sessionsLeft: pkg.sessionsTotal,
      purchasedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + pkg.validityDays * 24 * 60 * 60 * 1000).toISOString(),
      pricePaid: pkg.price
    }

    // Save Client Package
    const updated = [newPurchase, ...clientPackages]
    setClientPackages(updated)
    db.saveClientPackages(updated)

    // Save Payment Log
    const currentPayments = db.getPayments()
    db.savePayments([...currentPayments, {
      id: `pay-${Date.now()}`,
      clientId: cl.id,
      clientName: `${cl.firstName} ${cl.lastName}`,
      type: 'package',
      referenceId: newPurchase.id,
      referenceName: pkg.name,
      amount: pkg.price,
      method: 'BANK_TRANSFER',
      status: 'PAID',
      createdAt: new Date().toISOString()
    }])

    setShowNewPurchase(false)
    setClientId('')
    setPurchasePkgId('')
  }

  const consumeSession = (id: string) => {
    const updated = clientPackages.map(cp => {
      if (cp.id === id && cp.sessionsLeft > 0) {
        return {
          ...cp,
          sessionsUsed: cp.sessionsUsed + 1,
          sessionsLeft: cp.sessionsLeft - 1
        }
      }
      return cp
    })
    setClientPackages(updated)
    db.saveClientPackages(updated)
  }

  const fmtCLP = (n: number) => `$${n.toLocaleString('es-CL')}`

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[hsl(220,18%,96%)] p-4 gap-4">
      <div className="flex items-center justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-lg font-bold text-slate-800">Bonos y Paquetes Sesiones</h1>
          <p className="text-xs text-slate-500">Gestión de paquetes de tratamiento prepagados</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowNewOffer(true)}
            className="flex items-center gap-1.5 px-3 h-9 text-xs font-semibold rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition shadow-sm">
            <Plus className="h-4 w-4" /> Crear Oferta Bono
          </button>
          <button onClick={() => setShowNewPurchase(true)}
            className="flex items-center gap-1.5 px-3 h-9 text-xs font-semibold rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 transition shadow-sm">
            <ShoppingBag className="h-4 w-4" /> Vender a Cliente
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 shrink-0 border-b border-slate-200">
        <div className="flex gap-1">
          <button onClick={() => setActiveTab('offers')}
            className={cn("px-4 py-2 text-xs font-semibold border-b-2 transition-all", activeTab === 'offers' ? "border-cyan-600 text-cyan-600 font-bold" : "border-transparent text-slate-500 hover:text-slate-700")}>
            Planes y Ofertas de Bonos
          </button>
          <button onClick={() => setActiveTab('purchases')}
            className={cn("px-4 py-2 text-xs font-semibold border-b-2 transition-all", activeTab === 'purchases' ? "border-cyan-600 text-cyan-600 font-bold" : "border-transparent text-slate-500 hover:text-slate-700")}>
            Suscripciones de Clientes
          </button>
        </div>
        <div className="relative w-48 mb-1.5">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar..."
            className="w-full h-8 pl-8 pr-2.5 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {activeTab === 'offers' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredOffers.map(pkg => (
              <div key={pkg.id} className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 hover:shadow-md transition-all flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200 px-2.5 py-0.5 rounded-full capitalize">
                      {pkg.sessionsTotal} Sesiones
                    </span>
                    <span className="text-xs font-bold text-cyan-600">{fmtCLP(pkg.price)}</span>
                  </div>
                  <h3 className="font-bold text-sm text-slate-800 mt-2.5">{pkg.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{pkg.description}</p>
                  <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5 text-xs text-slate-600">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Servicio Base:</span>
                      <span className="font-medium text-slate-700">{pkg.serviceName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Validez:</span>
                      <span className="font-medium text-slate-700">{pkg.validityDays} días</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                  <span className="flex items-center gap-1">
                    <Gift className="h-3.5 w-3.5 text-violet-500" /> Código: {pkg.id}
                  </span>
                  <span className="text-emerald-600 font-semibold">Oferta Activa</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50/75 text-slate-500 font-bold border-b border-slate-100">
                  <th className="p-4">Cliente</th>
                  <th className="p-4">Paquete Suscrito</th>
                  <th className="p-4">Sesiones Consumidas</th>
                  <th className="p-4">Sesiones Restantes</th>
                  <th className="p-4">Precio Pagado</th>
                  <th className="p-4">Expiración</th>
                  <th className="p-4 text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPurchases.map(cp => (
                  <tr key={cp.id} className="hover:bg-slate-50/50 transition">
                    <td className="p-4 font-semibold text-slate-800">{cp.clientName}</td>
                    <td className="p-4">{cp.packageName}</td>
                    <td className="p-4 text-slate-500">{cp.sessionsUsed} usadas</td>
                    <td className="p-4">
                      <span className={cn("px-2 py-0.5 font-bold rounded", cp.sessionsLeft > 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700")}>
                        {cp.sessionsLeft} restantes
                      </span>
                    </td>
                    <td className="p-4 font-medium text-slate-700">{fmtCLP(cp.pricePaid)}</td>
                    <td className="p-4 text-slate-400">{new Date(cp.expiresAt).toLocaleDateString('es-CL')}</td>
                    <td className="p-4 text-right">
                      <button onClick={() => consumeSession(cp.id)} disabled={cp.sessionsLeft === 0}
                        className="px-2.5 h-7 text-[10px] font-bold rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-45 transition">
                        Gastar Sesión
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Offer Modal */}
      {showNewOffer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowNewOffer(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-800 mb-4">Crear Oferta de Bono</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Nombre del Paquete *</label>
                <input type="text" placeholder="Ej: Tratamiento Reductor x5" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Descripción</label>
                <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" rows={2} value={description} onChange={e => setDescription(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Servicio Base *</label>
                <select className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={serviceId} onChange={e => setServiceId(e.target.value)}>
                  <option value="">Seleccione servicio...</option>
                  {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Cant. Sesiones *</label>
                  <input type="number" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={sessionsTotal} onChange={e => setSessionsTotal(Number(e.target.value))} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Validez (días) *</label>
                  <input type="number" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={validityDays} onChange={e => setValidityDays(Number(e.target.value))} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Precio Total Promocional (CLP) *</label>
                <input type="number" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={price} onChange={e => setPrice(Number(e.target.value))} />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowNewOffer(false)} className="flex-1 h-9 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
              <button onClick={handleCreateOffer} disabled={!name || !serviceId} className="flex-1 h-9 bg-cyan-600 text-white rounded-xl text-sm font-semibold hover:bg-cyan-700 transition disabled:opacity-50">Guardar Plan</button>
            </div>
          </div>
        </div>
      )}

      {/* New Purchase Modal */}
      {showNewPurchase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowNewPurchase(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-800 mb-4">Vender Suscripción/Bono</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Asignar a Cliente *</label>
                <select className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={clientId} onChange={e => setClientId(e.target.value)}>
                  <option value="">Seleccione cliente...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Paquete de Tratamientos *</label>
                <select className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={purchasePkgId} onChange={e => setPurchasePkgId(e.target.value)}>
                  <option value="">Seleccione bono...</option>
                  {packages.map(p => <option key={p.id} value={p.id}>{p.name} ({p.sessionsTotal} ses. - {fmtCLP(p.price)})</option>)}
                </select>
              </div>
              <p className="text-[10px] text-slate-400 mt-2">
                * Esto generará automáticamente un registro de pago con el método de transferencia bancaria por defecto.
              </p>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowNewPurchase(false)} className="flex-1 h-9 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
              <button onClick={handlePurchase} disabled={!clientId || !purchasePkgId} className="flex-1 h-9 bg-cyan-600 text-white rounded-xl text-sm font-semibold hover:bg-cyan-700 transition disabled:opacity-50">Vender Paquete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
